/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { readProductField, AMAZON_URL_FIELD } from '@/lib/product/product-record-fields';

/**
 * "Shop on Amazon" CTA (UCR-2697) — links to the product record's `amazonURL`. Renders nothing
 * when the field is empty. External link, opens in a new tab.
 */
export default function ShopOnAmazon({
    product,
    className,
}: {
    product: ShopperProducts.schemas['Product'];
    className?: string;
}): ReactElement | null {
    const { t } = useTranslation('product');
    const url = readProductField(product, AMAZON_URL_FIELD);

    if (!url) {
        return null;
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-slot="pdp-shop-on-amazon"
            className={cn(
                'flex w-[318px] max-w-full items-center justify-between gap-4 bg-bm-green px-6 py-3 font-condensed text-xl font-bold uppercase leading-[0.85] text-bm-ink transition-colors hover:bg-bm-green-600 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
                className
            )}>
            {t('shopOnAmazon', { defaultValue: 'Shop on Amazon' })}
            {/* Screen-reader-only cue so the off-site/new-tab transition isn't a surprise (UCR-2771 AC).
                The visible "Amazon" label already signals off-site to sighted users; the design keeps the
                plain arrow, so no visible external-link icon is added. */}
            <span className="sr-only"> {t('newTabScreenReaderText', { defaultValue: '(opens in a new tab)' })}</span>
            <ArrowRight aria-hidden="true" className="size-5 shrink-0" />
        </a>
    );
}
